'use client'
import { useState } from 'react'
import { useFormspree } from '@/hooks/useFormspree'
import { trackEvent } from '@/lib/posthog'
import { FORMSPREE_FORM_IDS, POSTHOG_EVENTS } from '@/lib/constants'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^\d{10}$/

interface FieldErrors {
  company_name?: string
  contact_name?: string
  email?: string
  phone?: string
  origin?: string
  destination?: string
}

export function ShipperForm() {
  const { status, error, submit } = useFormspree(FORMSPREE_FORM_IDS.SHIPPERS)
  const [fields, setFields] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    freight_mode: 'Dry Van',
    monthly_loads: '1–5',
    payment_terms: 'Net 30',
    special_requirements: '',
    tcpaConsent: false,
  })
  const [errors, setErrors] = useState<FieldErrors>({})

  function set(key: string, value: string) {
    setFields(prev => ({ ...prev, [key]: value }))
  }

  function validate(): boolean {
    const errs: FieldErrors = {}
    if (!fields.company_name.trim()) errs.company_name = 'Company name is required'
    if (!fields.contact_name.trim()) errs.contact_name = 'Contact name is required'
    if (!EMAIL_RE.test(fields.email)) errs.email = 'Enter a valid email address'
    if (!PHONE_RE.test(fields.phone.replace(/\D/g, '')))
      errs.phone = 'Enter a 10-digit phone number'
    if (!fields.origin.trim()) errs.origin = 'Origin is required'
    if (!fields.destination.trim()) errs.destination = 'Destination is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    submit({ ...fields })
    trackEvent(POSTHOG_EVENTS.SHIPPER_INQUIRY, { freight_mode: fields.freight_mode })
  }

  if (status === 'success') {
    return (
      <div className="glass-card rounded-2xl p-10 text-center">
        <p className="text-white text-xl font-semibold">Thank you — we&apos;ll be in touch!</p>
        <p className="text-[color:var(--foreground-muted)] mt-2 text-sm">
          Expect a response within one business day.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Shipper inquiry form"
      noValidate
      className="glass-card rounded-2xl p-8 flex flex-col gap-5"
    >
      <div aria-live="polite" aria-atomic="true" className="min-h-[1.25rem]">
        {error && <p className="text-red-400 text-sm font-medium">{error}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="sh-company_name" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Company Name <span aria-hidden="true">*</span>
          </label>
          <input
            id="sh-company_name"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.company_name ? ' border border-red-500' : ''}`}
            value={fields.company_name}
            onChange={e => set('company_name', e.target.value)}
            autoComplete="organization"
          />
          {errors.company_name && <p className="text-red-400 text-xs">{errors.company_name}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="sh-contact_name" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Contact Name <span aria-hidden="true">*</span>
          </label>
          <input
            id="sh-contact_name"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.contact_name ? ' border border-red-500' : ''}`}
            value={fields.contact_name}
            onChange={e => set('contact_name', e.target.value)}
            autoComplete="name"
          />
          {errors.contact_name && <p className="text-red-400 text-xs">{errors.contact_name}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="sh-email" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Email <span aria-hidden="true">*</span>
          </label>
          <input
            id="sh-email"
            type="email"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.email ? ' border border-red-500' : ''}`}
            value={fields.email}
            onChange={e => set('email', e.target.value)}
            autoComplete="email"
          />
          {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="sh-phone" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Phone <span aria-hidden="true">*</span>
          </label>
          <input
            id="sh-phone"
            type="tel"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.phone ? ' border border-red-500' : ''}`}
            value={fields.phone}
            onChange={e => set('phone', e.target.value)}
            autoComplete="tel"
            placeholder="10-digit number"
          />
          {errors.phone && <p className="text-red-400 text-xs">{errors.phone}</p>}
        </div>
      </div>

      <p className="text-xs text-gray-500 max-w-prose">
        By providing your phone number, you agree to receive SMS messages from ADG2 Transport Corp for service updates and communication. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out, HELP for assistance.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="sh-origin" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Typical Origin <span aria-hidden="true">*</span>
          </label>
          <input
            id="sh-origin"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.origin ? ' border border-red-500' : ''}`}
            value={fields.origin}
            onChange={e => set('origin', e.target.value)}
            placeholder="City, State"
          />
          {errors.origin && <p className="text-red-400 text-xs">{errors.origin}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="sh-destination" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Typical Destination <span aria-hidden="true">*</span>
          </label>
          <input
            id="sh-destination"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.destination ? ' border border-red-500' : ''}`}
            value={fields.destination}
            onChange={e => set('destination', e.target.value)}
            placeholder="City, State"
          />
          {errors.destination && <p className="text-red-400 text-xs">{errors.destination}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="sh-freight_mode" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Freight Mode
          </label>
          <select
            id="sh-freight_mode"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.freight_mode}
            onChange={e => set('freight_mode', e.target.value)}
          >
            <option value="Dry Van">Dry Van</option>
            <option value="Reefer">Reefer</option>
            <option value="Power Only">Power Only</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="sh-monthly_loads" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Monthly Loads
          </label>
          <select
            id="sh-monthly_loads"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.monthly_loads}
            onChange={e => set('monthly_loads', e.target.value)}
          >
            <option value="1–5">1–5</option>
            <option value="6–20">6–20</option>
            <option value="21–50">21–50</option>
            <option value="50+">50+</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="sh-payment_terms" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Payment Terms
          </label>
          <select
            id="sh-payment_terms"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.payment_terms}
            onChange={e => set('payment_terms', e.target.value)}
          >
            <option value="Net 15">Net 15</option>
            <option value="Net 30">Net 30</option>
            <option value="Net 45">Net 45</option>
            <option value="Quick Pay">Quick Pay</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="sh-special_requirements" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Special Requirements
        </label>
        <textarea
          id="sh-special_requirements"
          rows={3}
          className="glass-input rounded-lg px-4 py-3 text-white resize-none"
          value={fields.special_requirements}
          onChange={e => set('special_requirements', e.target.value)}
          placeholder="Temperature range, hazmat, liftgate, etc."
        />
      </div>

      <label className="flex items-start gap-3 text-sm text-[color:var(--foreground-muted)] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[color:var(--accent)] flex-shrink-0"
          checked={fields.tcpaConsent}
          onChange={e => setFields(prev => ({ ...prev, tcpaConsent: e.target.checked }))}
          aria-required="true"
        />
        <span>
          I agree to receive transactional SMS messages from ADG2 Transport Corp regarding my inquiry. Message and data rates may apply. Reply STOP to opt out.{' '}
          <a href="/privacy-policy" className="underline hover:text-white transition-colors">Privacy Policy</a>
          {' '}·{' '}
          <a href="/terms-of-service" className="underline hover:text-white transition-colors">Terms</a>
        </span>
      </label>

      <button
        type="submit"
        disabled={status === 'loading' || !fields.tcpaConsent}
        className="glass-button rounded-xl px-8 py-4 font-semibold text-base mt-2 disabled:opacity-50"
      >
        {status === 'loading' ? 'Sending…' : 'Start Shipping with ADG2'}
      </button>
    </form>
  )
}
